import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  listOrphanComments,
  getOrphanCommentsStats,
  deleteOrphanComment,
  bulkDeleteOrphanComments,
} from '../../services/adminApi';
import { notify, confirmAsync } from '../../utils/notify';
import './OrphanComments.css';

// Reason filter options. ``''`` (empty) means "all reasons".
const REASON_OPTIONS = [
  { value: '', labelKey: 'admin.orphanComments.filterAll' },
  { value: 'reply_failed', labelKey: 'admin.orphanComments.reasonReplyFailed' },
  { value: 'parent_post_missing', labelKey: 'admin.orphanComments.reasonParentMissing' },
  { value: 'page_disconnected', labelKey: 'admin.orphanComments.reasonPageDisconnected' },
];

const PAGE_SIZE = 50;

function OrphanComments() {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);
  const isSuperadmin = currentUser?.role === 'superadmin';

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [returned, setReturned] = useState(0);
  const [page, setPage] = useState(1);
  const [reason, setReason] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busyDeleteId, setBusyDeleteId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const res = await getOrphanCommentsStats();
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load orphan-comments stats:', err);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listOrphanComments({
        page,
        page_size: PAGE_SIZE,
        reason: reason || undefined,
      });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setReturned(res.data?.returned || 0);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Failed to load orphan comments:', err);
      setError(err.response?.data?.detail || t('admin.orphanComments.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, reason, t]);

  useEffect(() => {
    if (isSuperadmin) {
      loadStats();
    }
  }, [isSuperadmin, loadStats]);

  useEffect(() => {
    if (isSuperadmin) {
      loadItems();
    }
  }, [isSuperadmin, loadItems]);

  if (!isSuperadmin) {
    return (
      <div className="orphan-comments-page">
        <div className="orphan-error">{t('admin.orphanComments.superadminOnly')}</div>
      </div>
    );
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = items.every((c) => next.has(c.id));
      if (allSelected) {
        items.forEach((c) => next.delete(c.id));
      } else {
        items.forEach((c) => next.add(c.id));
      }
      return next;
    });
  };

  const handleDeleteOne = async (commentId) => {
    if (!(await confirmAsync({ message: t('admin.orphanComments.confirmDeleteOne'), danger: true }))) return;
    setBusyDeleteId(commentId);
    try {
      await deleteOrphanComment(commentId);
      // Optimistic local removal — server already deleted
      setItems((prev) => prev.filter((c) => c.id !== commentId));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(commentId);
        return next;
      });
      loadStats();
    } catch (err) {
      console.error('Failed to delete orphan comment:', err);
      notify.error(err.response?.data?.detail || t('admin.orphanComments.deleteError'));
    } finally {
      setBusyDeleteId(null);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!(await confirmAsync({ message: t('admin.orphanComments.confirmDeleteBulk', { count: ids.length }), danger: true }))) return;
    setBulkBusy(true);
    try {
      const res = await bulkDeleteOrphanComments(ids);
      const deleted = new Set(res.data?.deleted || []);
      setItems((prev) => prev.filter((c) => !deleted.has(c.id)));
      setSelectedIds(new Set());
      const skipped = res.data?.skipped_count || 0;
      if (skipped > 0) {
        notify.warning(t('admin.orphanComments.bulkSkippedNotice', {
          deleted: deleted.size,
          skipped,
        }));
      }
      loadStats();
    } catch (err) {
      console.error('Failed to bulk delete orphan comments:', err);
      notify.error(err.response?.data?.detail || t('admin.orphanComments.deleteError'));
    } finally {
      setBulkBusy(false);
    }
  };

  const reasonBadge = (r) => {
    const classByReason = {
      reply_failed: 'orphan-badge-failed',
      parent_post_missing: 'orphan-badge-missing',
      page_disconnected: 'orphan-badge-disconnected',
    };
    return (
      <span className={`orphan-badge ${classByReason[r] || ''}`}>
        {t(`admin.orphanComments.reason_${r}`)}
      </span>
    );
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="orphan-comments-page">
      <div className="orphan-header">
        <h2>{t('admin.orphanComments.title')}</h2>
        <p className="orphan-subtitle">{t('admin.orphanComments.subtitle')}</p>
      </div>

      {/* Stats summary */}
      {stats && (
        <div className="orphan-stats">
          <div className="orphan-stat-card">
            <div className="orphan-stat-label">{t('admin.orphanComments.statReplyFailed')}</div>
            <div className="orphan-stat-value">{stats.reply_failed}</div>
          </div>
          <div className="orphan-stat-card">
            <div className="orphan-stat-label">{t('admin.orphanComments.statParentMissingSample')}</div>
            <div className="orphan-stat-value">~{stats.parent_post_missing_in_sample}</div>
            <div className="orphan-stat-note">
              {t('admin.orphanComments.sampleNote', { size: stats.sample_size })}
            </div>
          </div>
          <div className="orphan-stat-card">
            <div className="orphan-stat-label">{t('admin.orphanComments.statPageDisconnectedSample')}</div>
            <div className="orphan-stat-value">~{stats.page_disconnected_in_sample}</div>
            <div className="orphan-stat-note">
              {t('admin.orphanComments.sampleNote', { size: stats.sample_size })}
            </div>
          </div>
        </div>
      )}

      {/* Filters + bulk actions */}
      <div className="orphan-toolbar">
        <div className="orphan-filters">
          <label>
            {t('admin.orphanComments.filterReasonLabel')}
            <select
              value={reason}
              onChange={(e) => { setReason(e.target.value); setPage(1); }}
            >
              {REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="orphan-btn orphan-btn-secondary"
            onClick={() => { loadItems(); loadStats(); }}
            disabled={loading}
          >
            ↻ {t('admin.orphanComments.refresh')}
          </button>
        </div>
        <div className="orphan-bulk-actions">
          <span className="orphan-selection-count">
            {t('admin.orphanComments.selectionCount', { count: selectedIds.size })}
          </span>
          <button
            type="button"
            className="orphan-btn orphan-btn-danger"
            onClick={handleBulkDelete}
            disabled={!selectedIds.size || bulkBusy}
          >
            🗑 {t('admin.orphanComments.bulkDelete')}
          </button>
        </div>
      </div>

      {error && <div className="orphan-error">{error}</div>}

      {/* Table */}
      {loading ? (
        <div className="orphan-loading">{t('common.loading')}</div>
      ) : items.length === 0 ? (
        <div className="orphan-empty">
          <span className="orphan-empty-icon">✨</span>
          <div>{t('admin.orphanComments.empty')}</div>
        </div>
      ) : (
        <>
          <table className="orphan-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={items.length > 0 && items.every((c) => selectedIds.has(c.id))}
                    onChange={toggleSelectAllVisible}
                  />
                </th>
                <th>ID</th>
                <th>{t('admin.orphanComments.colReason')}</th>
                <th>{t('admin.orphanComments.colUser')}</th>
                <th>{t('admin.orphanComments.colCommenter')}</th>
                <th>{t('admin.orphanComments.colContent')}</th>
                <th>{t('admin.orphanComments.colExternalId')}</th>
                <th>{t('admin.orphanComments.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => toggleSelect(c.id)}
                    />
                  </td>
                  <td className="orphan-id">{c.id}</td>
                  <td>{reasonBadge(c.orphan_reason)}</td>
                  <td className="orphan-mono">user_{c.user_id}</td>
                  <td>{c.external_username || '—'}</td>
                  <td className="orphan-content" title={c.content}>
                    {c.content || '—'}
                  </td>
                  <td className="orphan-mono orphan-external-id">{c.external_comment_id || '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="orphan-btn orphan-btn-danger orphan-btn-sm"
                      onClick={() => handleDeleteOne(c.id)}
                      disabled={busyDeleteId === c.id}
                    >
                      {busyDeleteId === c.id ? '...' : t('admin.orphanComments.deleteOne')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="orphan-pagination">
            <span>
              {t('admin.orphanComments.paginationLabel', {
                returned,
                total,
                page,
                totalPages,
              })}
            </span>
            <div>
              <button
                type="button"
                className="orphan-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                ‹ {t('common.previous')}
              </button>
              <button
                type="button"
                className="orphan-btn"
                onClick={() => setPage((p) => p + 1)}
                disabled={returned < PAGE_SIZE}
              >
                {t('common.next')} ›
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default OrphanComments;
